import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { getUser } from "redux/features/userSliceWithTokenValidation";
import { useUpdateDossierMutation } from "actions/dossiers/mutations";
import { DEFAULT_API } from "routes/api/endpoints";
import { toastApiErrors } from "utils/toastApiError";
import GeneriqueModal from "./GeneriqueModal";
import ButtonCancel from "./buttons/ButtonCancel";
import ButtonGreen from "./buttons/ButtonGreen";

const FORMES_JURIDIQUES = [
  "Société en nom collectif (SNC)",
  "Société en commandite simple (SCS)",
  "Société à responsabilité limitée (SARL)",
  "Société Unipersonnelle à Responsabilité Limitée (SUARL)",
  "Société anonyme (SA)",
  "Société par actions simplifiée (SAS)",
  "SAS Unipersonnelle (SASU)",
  "Société en commandite par actions (SCA)",
  "Société civile professionnelle (SCP)",
  "Société civile immobilière (SCI)",
  "Société civile (autre)",
  "Groupement d'intérêt économique (GIE)",
  "Entreprise Individuelle",
  "AUTRE",
];

const inputStyle = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: "8px",
  border: "1px solid #ccc",
  fontSize: "13px",
  fontFamily: "Montserrat",
};

const labelStyle = {
  fontSize: "13px",
  fontWeight: 600,
  marginBottom: "4px",
  color: "#333",
};

const fieldStyle = {
  display: "flex",
  flexDirection: "column",
};

const mediaUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  try {
    return new URL(path, DEFAULT_API).href;
  } catch {
    return path;
  }
};

const EditDossierModal = ({ isOpen, onClose, onUpdated, dossier }) => {
  const userStatus = useSelector(getUser);
  const [updateDossier, { isLoading }] = useUpdateDossierMutation();
  const [formData, setFormData] = useState({});
  const [logoFile, setLogoFile] = useState(null);
  const [cachetFile, setCachetFile] = useState(null);

  useEffect(() => {
    if (dossier) {
      setFormData({
        nom_entreprise: dossier.nom_entreprise || "",
        forme_juridique: dossier.forme_juridique || "",
        secteur_activite: dossier.secteur_activite || "",
        capital_social: dossier.capital_social || 0,
        description: dossier.description || "",
        adresse: dossier.adresse || "",
        code_postal: dossier.code_postal || "",
        ville: dossier.ville || "",
        region: dossier.region || "",
        pays: dossier.pays || "",
        telephone: dossier.telephone || "",
        email: dossier.email || "",
        site_internet: dossier.site_internet || "",
      });
    }
  }, [dossier]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!dossier) return;
    try {
      let body = formData;
      if (logoFile || cachetFile) {
        body = new FormData();
        Object.entries(formData).forEach(([k, v]) => {
          if (v !== undefined && v !== null) body.append(k, v);
        });
        if (logoFile) body.append('logo', logoFile);
        if (cachetFile) body.append('cachet', cachetFile);
      }
      await updateDossier({
        id: dossier.id,
        body,
        token: userStatus?.access,
      }).unwrap();
      toast.success("Entreprise modifiée avec succès");
      if (onUpdated) onUpdated();
      onClose();
    } catch (error) {
      console.error("Erreur modification dossier:", error);
      toastApiErrors(error);
    }
  };

  return (
    <GeneriqueModal
      className="modal-overlay"
      onClose={onClose}
      title={"Modifier l'entreprise"}
      isOpen={isOpen}
    >
      <form onSubmit={handleSubmit} style={{ minWidth: "500px" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "16px",
            padding: "10px",
          }}
        >
          <div style={fieldStyle}>
            <label style={labelStyle}>Raison sociale *</label>
            <input
              type="text"
              name="nom_entreprise"
              value={formData.nom_entreprise || ""}
              onChange={handleChange}
              style={inputStyle}
              required
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Forme juridique</label>
            <select
              name="forme_juridique"
              value={formData.forme_juridique || ""}
              onChange={handleChange}
              style={inputStyle}
            >
              <option value="">Sélectionnez une forme</option>
              {FORMES_JURIDIQUES.map((forme) => (
                <option key={forme} value={forme}>
                  {forme === "AUTRE" ? "Autre" : forme}
                </option>
              ))}
            </select>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Secteur d&apos;activité</label>
            <input
              type="text"
              name="secteur_activite"
              value={formData.secteur_activite || ""}
              onChange={handleChange}
              style={inputStyle}
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Capital social</label>
            <input
              type="number"
              name="capital_social"
              value={formData.capital_social || 0}
              onChange={handleChange}
              min="0"
              style={inputStyle}
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Adresse *</label>
            <input
              type="text"
              name="adresse"
              value={formData.adresse || ""}
              onChange={handleChange}
              style={inputStyle}
              required
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Code postal</label>
            <input
              type="text"
              name="code_postal"
              value={formData.code_postal || ""}
              onChange={handleChange}
              style={inputStyle}
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Ville</label>
            <input
              type="text"
              name="ville"
              value={formData.ville || ""}
              onChange={handleChange}
              style={inputStyle}
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Région</label>
            <input
              type="text"
              name="region"
              value={formData.region || ""}
              onChange={handleChange}
              style={inputStyle}
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Pays</label>
            <input
              type="text"
              name="pays"
              value={formData.pays || ""}
              onChange={handleChange}
              style={inputStyle}
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Téléphone *</label>
            <input
              type="text"
              name="telephone"
              value={formData.telephone || ""}
              onChange={handleChange}
              style={inputStyle}
              required
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Email *</label>
            <input
              type="email"
              name="email"
              value={formData.email || ""}
              onChange={handleChange}
              style={inputStyle}
              required
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Site internet</label>
            <input
              type="text"
              name="site_internet"
              value={formData.site_internet || ""}
              onChange={handleChange}
              style={inputStyle}
            />
          </div>

          <div style={{ ...fieldStyle, gridColumn: "1 / 3" }}>
            <label style={labelStyle}>Description</label>
            <textarea
              name="description"
              value={formData.description || ""}
              onChange={handleChange}
              style={{ ...inputStyle, minHeight: "60px" }}
              rows={2}
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Logo (factures/devis)</label>
            {dossier?.logo && !logoFile && (
              <img src={mediaUrl(dossier.logo)} alt="logo actuel"
                style={{ maxHeight: 40, maxWidth: 120, objectFit: 'contain', marginBottom: 6 }} />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
              style={{ ...inputStyle, padding: '6px' }}
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Cachet / Signature (factures)</label>
            {dossier?.cachet && !cachetFile && (
              <img src={mediaUrl(dossier.cachet)} alt="cachet actuel"
                style={{ maxHeight: 40, maxWidth: 120, objectFit: 'contain', marginBottom: 6 }} />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setCachetFile(e.target.files?.[0] || null)}
              style={{ ...inputStyle, padding: '6px' }}
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
            marginTop: "20px",
            padding: "0 10px",
          }}
        >
          <ButtonCancel text="Annuler" onClick={onClose} />
          <ButtonGreen text={isLoading ? "Enregistrement..." : "Enregistrer"} type="submit" />
        </div>
      </form>
    </GeneriqueModal>
  );
};

export default EditDossierModal;
